import { useCallback, useEffect, useState } from "react";

import type { GlobalPeriod } from "./globalFilters";

/**
 * MER-164: per-user dashboard preferences for v1.
 *
 * Storage choice: `localStorage` keyed by Supabase user id + customer
 * slug. The ticket also suggests a Supabase `user_preferences` table
 * for multi-device sync; that's a v2 follow-up (filing as a separate
 * ticket once we know users want it). LocalStorage satisfies AC #3
 * (Save persists) and #4 (Reload applies the saved value).
 *
 * Each user's preferences live under `meridian:prefs:<userId>:<slug>`,
 * so the same browser logged in as different users keeps the
 * settings separated. Falls back to anonymous prefs (under `anon:`)
 * when there's no session yet.
 */

export interface NotificationToggles {
  freshness: boolean;
  redFlag: boolean;
  syncFailure: boolean;
  weeklyDigest: boolean;
}

export interface UserPreferences {
  fullName: string;
  defaultLandingTab: string | null;
  defaultPeriod: GlobalPeriod | null;
  notifications: NotificationToggles;
}

const DEFAULTS: UserPreferences = {
  fullName: "",
  defaultLandingTab: null,
  defaultPeriod: null,
  notifications: {
    freshness: false,
    redFlag: false,
    syncFailure: false,
    weeklyDigest: false,
  },
};

function storageKey(userId: string | null, slug: string): string {
  const u = userId ?? "anon";
  return `meridian:prefs:${u}:${slug}`;
}

export function loadPrefs(userId: string | null, slug: string): UserPreferences {
  try {
    const raw = window.localStorage.getItem(storageKey(userId, slug));
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<UserPreferences>;
    return {
      ...DEFAULTS,
      ...parsed,
      notifications: {
        ...DEFAULTS.notifications,
        ...(parsed.notifications ?? {}),
      },
    };
  } catch {
    return DEFAULTS;
  }
}

export function savePrefs(userId: string | null, slug: string, prefs: UserPreferences): void {
  try {
    window.localStorage.setItem(storageKey(userId, slug), JSON.stringify(prefs));
  } catch {
    // localStorage unavailable / quota; caller falls back to in-memory.
  }
}

/**
 * Read-write hook for the user's preferences. Persists changes to
 * localStorage on every `setPrefs` call so the Save button in
 * Settings.tsx is a simple `setPrefs(next)`.
 */
export function useUserPreferences(
  userId: string | null,
  slug: string | undefined,
): {
  prefs: UserPreferences;
  setPrefs: (next: UserPreferences) => void;
} {
  const [prefs, setPrefsState] = useState<UserPreferences>(() =>
    slug ? loadPrefs(userId, slug) : DEFAULTS,
  );

  useEffect(() => {
    if (slug) setPrefsState(loadPrefs(userId, slug));
  }, [userId, slug]);

  const setPrefs = useCallback(
    (next: UserPreferences) => {
      if (slug) savePrefs(userId, slug, next);
      setPrefsState(next);
    },
    [userId, slug],
  );

  return { prefs, setPrefs };
}
